import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Input, Select, Typography } from '@arco-design/web-react';

import { isFieldInteractionLocked } from '../../platform/capability-registry/fieldControl';
import { isFieldVisible } from '../../platform/capability-registry/resolveFieldMode';
import { getNodeConfig } from '../bare-metal-nodes/api';
import { useInstanceConfig } from '../power-policy/hooks';
import { PROVISIONING_MODE_OPTIONS, getBootModeDescription } from './constants';
import { BARE_METAL_PROVISIONING_FIELD_KEYS } from './fieldKeys';
import styles from './index.module.less';
import { pickConfiguredArchTypes, resolveArchOptions } from './resolveArchOptions';
import { resolveBootOptions, resolveDefaultBootMode } from './resolveBootOptions';
import type { BareMetalProvisioningBlockProps, BareMetalProvisioningMode, BareMetalProvisioningPaths } from './types';

const FormItem = Form.Item;

const SSH_PRIVATE_KEY_PLACEHOLDER = '请输入与目标主机公钥匹配的 SSH 私钥，请确保内容完整，包含 BEGIN/END 标识';

/** 裸金属系统交付方式：Install / Adopt，以及 Install 下的引导方式与 Adopt 凭据 */
export function BareMetalProvisioningBlock(props: BareMetalProvisioningBlockProps) {
  const { form, paths: rawPaths, options, ...control } = props;
  const paths = rawPaths as BareMetalProvisioningPaths;
  const { t } = useTranslation();
  const config = useInstanceConfig();
  const [nodeArchTypes, setNodeArchTypes] = useState<string[] | undefined>();
  const isPreview = control.blockMode === 'preview';

  const provisioningMode = (Form.useWatch(paths.provisioningMode, form) as BareMetalProvisioningMode | undefined) || 'Install';
  const isAdopt = provisioningMode === 'Adopt';
  const isInstall = !isAdopt;

  const bootOptions = useMemo(
    () => resolveBootOptions(options?.bootOptions ?? (config?.bootOptions as string[] | undefined)),
    [config?.bootOptions, options?.bootOptions],
  );
  const defaultBootMode = useMemo(() => resolveDefaultBootMode(bootOptions), [bootOptions]);
  const configuredArchTypes = useMemo(
    () => pickConfiguredArchTypes(options?.archTypes, config?.archTypes as string[] | undefined, nodeArchTypes),
    [config?.archTypes, nodeArchTypes, options?.archTypes],
  );
  const archOptions = useMemo(() => resolveArchOptions(configuredArchTypes), [configuredArchTypes]);

  useEffect(() => {
    if (!isAdopt) return;
    if (pickConfiguredArchTypes(options?.archTypes, config?.archTypes as string[] | undefined)) return;
    let cancelled = false;
    void getNodeConfig()
      .then((res) => {
        if (cancelled) return;
        const list = (res as { archTypes?: string[] } | undefined)?.archTypes;
        if (Array.isArray(list)) setNodeArchTypes(list);
      })
      .catch((error) => {
        console.log(error, 'get node config archTypes error');
      });
    return () => {
      cancelled = true;
    };
  }, [config?.archTypes, isAdopt, options?.archTypes]);

  const provisioningLocked =
    options?.lockProvisioningMode === true || isFieldInteractionLocked(BARE_METAL_PROVISIONING_FIELD_KEYS.provisioningMode, control);
  const bootLocked = isFieldInteractionLocked(BARE_METAL_PROVISIONING_FIELD_KEYS.bootMode, control);
  const accountPasswdLocked = isPreview || isFieldInteractionLocked(BARE_METAL_PROVISIONING_FIELD_KEYS.accountPasswd, control);
  const accountPrivatedKeyLocked = isPreview || isFieldInteractionLocked(BARE_METAL_PROVISIONING_FIELD_KEYS.accountPrivatedKey, control);
  const architectureLocked = isPreview || isFieldInteractionLocked(BARE_METAL_PROVISIONING_FIELD_KEYS.systemArchitecture, control);
  const osLocked = isPreview || isFieldInteractionLocked(BARE_METAL_PROVISIONING_FIELD_KEYS.systemOs, control);
  const versionLocked = isPreview || isFieldInteractionLocked(BARE_METAL_PROVISIONING_FIELD_KEYS.systemVersion, control);

  const showBootMode = options?.showBootMode !== false && isInstall;
  /** SKU 创建不收集密码；架构/OS/版本仍展示以便写入 preset */
  const showAdoptCredentials = options?.showAdoptCredentials !== false;
  const requiredRules = useMemo(() => (isPreview ? undefined : [{ required: true, message: t('common:required') }]), [isPreview, t]);

  useEffect(() => {
    const current = form.getFieldValue(paths.provisioningMode);
    if (current == null || current === '') {
      form.setFieldValue(paths.provisioningMode, 'Install');
    }
  }, [form, paths.provisioningMode]);

  useEffect(() => {
    if (!isInstall) return;
    const current = String(form.getFieldValue(paths.bootMode) ?? '').trim();
    if (!current || !bootOptions.includes(current)) {
      form.setFieldValue(paths.bootMode, defaultBootMode);
    }
  }, [bootOptions, defaultBootMode, form, isInstall, paths.bootMode]);

  /** 配置选项到位后，清掉不在列表中的架构值，避免提交过期兜底选项 */
  useEffect(() => {
    if (!isAdopt) return;
    const current = String(form.getFieldValue(paths.systemArchitecture) ?? '').trim();
    if (current && !archOptions.includes(current)) {
      form.setFieldValue(paths.systemArchitecture, undefined);
    }
  }, [archOptions, form, isAdopt, paths.systemArchitecture]);

  if (!isFieldVisible(BARE_METAL_PROVISIONING_FIELD_KEYS.provisioningMode, control)) {
    return null;
  }

  return (
    <>
      <FormItem
        label="系统交付方式"
        field={paths.provisioningMode}
        initialValue="Install"
        disabled={provisioningLocked}
        rules={requiredRules}
      >
        <Select placeholder="请选择系统交付方式" disabled={provisioningLocked}>
          {PROVISIONING_MODE_OPTIONS.map((item) => (
            <Select.Option key={item.value} value={item.value}>
              <span>{item.label}</span>
              <span className={styles.optionDesc}>{item.description}</span>
            </Select.Option>
          ))}
        </Select>
      </FormItem>

      {showBootMode ? (
        <FormItem label="引导方式" field={paths.bootMode} initialValue={defaultBootMode} disabled={bootLocked} rules={requiredRules}>
          <Select placeholder="请选择引导方式" disabled={bootLocked}>
            {bootOptions.map((mode) => {
              const desc = getBootModeDescription(mode);
              return (
                <Select.Option key={mode} value={mode}>
                  <span>{mode}</span>
                  {desc ? <span className={styles.optionDesc}>{desc}</span> : null}
                </Select.Option>
              );
            })}
          </Select>
        </FormItem>
      ) : null}

      {isAdopt ? (
        <>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
            {showAdoptCredentials
              ? '系统不会被重新安装。请确认所选节点已经安装操作系统，并填写当前系统实际使用的网络地址和登录凭据。'
              : '系统不会被重新安装。请填写当前系统信息；登录凭据与静态 IP 在下单时填写。'}
          </Typography.Paragraph>

          {showAdoptCredentials ? (
            <>
              <FormItem label="登录用户">
                <Input value="root" disabled />
              </FormItem>
              <FormItem label="root 密码" field={paths.accountPasswd} disabled={accountPasswdLocked} rules={requiredRules}>
                <Input.Password placeholder="请输入 root 密码" disabled={accountPasswdLocked} autoComplete="new-password" />
              </FormItem>
              <FormItem label="SSH 私钥" field={paths.accountPrivatedKey} disabled={accountPrivatedKeyLocked} rules={requiredRules}>
                <Input.TextArea
                  placeholder={SSH_PRIVATE_KEY_PLACEHOLDER}
                  disabled={accountPrivatedKeyLocked}
                  autoSize={{ minRows: 3, maxRows: 8 }}
                />
              </FormItem>
            </>
          ) : null}
          <FormItem label={t('common:architecture')} field={paths.systemArchitecture} disabled={architectureLocked} rules={requiredRules}>
            <Select placeholder="请选择" disabled={architectureLocked} showSearch>
              {archOptions.map((arch) => (
                <Select.Option key={arch} value={arch}>
                  {arch}
                </Select.Option>
              ))}
            </Select>
          </FormItem>
          <FormItem label={t('common:operating.system')} field={paths.systemOs} disabled={osLocked} rules={requiredRules}>
            <Input placeholder="请输入" disabled={osLocked} />
          </FormItem>
          <FormItem label="操作系统版本" field={paths.systemVersion} disabled={versionLocked} rules={requiredRules}>
            <Input placeholder="请输入" disabled={versionLocked} />
          </FormItem>
        </>
      ) : null}
    </>
  );
}
